declare global {
  interface Window {
    gtag: any;
  }
}

import { UserItem, ViewerItem } from 'presentations/api/schema/user';
import { ProviderType } from 'domains/models/account/providerType';
import { PlanId } from 'domains/models/subscription/planId';
import Cookie from 'js-cookie';
import { PaymentMethod } from 'domains/models/event_payment/payment_method';
import { CalendarViewType } from 'domains/models/event_type/calendarViewType';
import { SyntheticEvent } from 'react';
import { currentLocale, currentTimeZone } from 'infras/datetime';
import { post } from 'infras/rest_client';
import { CreateInternalActivityLogBody } from 'presentations/api/schema/internal_activity_log';
import { apiPath } from 'presentations/routes/path';
import { Mixpanel } from './mixpanel';
import { Amplitude } from './amplitude';

type DimensionKey = 'plan';
type PlanKeyValue = PlanId; // planId

interface PurchaseItem {
  id: number;
  quantity: number;
  price: number;
  name: string;
  category?: string;
  variant?: string;
}

interface PurchaseServiceItem {
  id: number;
  quantity: number;
  price: number;
  name: string;
  category?: string;
  variant?: string;
}

export interface PurchaseData {
  transactionId: string;
  tax?: number;
  value: number;
  items: PurchaseItem[];
  planId: PlanId;
  currency?: string | null;
}

export interface PurchaseServiceData {
  transactionId: string;
  tax?: number;
  value: number;
  items: PurchaseServiceItem[];
  eventTypeId: number;
  currency?: string | null;
  paymentMethod: PaymentMethod;
  planId: PlanId;
  calendarViewType: CalendarViewType;
}
const isGoogle = !!process.env['GA_TRACKING_ID'];

export function pageview(url: string) {
  if (isGoogle) {
    try {
      window.gtag('config', process.env['GA_TRACKING_ID'], {
        page_path: url,
      });
    } catch (error) {
      console.debug(error);
    }
  }
}

export const setDimension = (key: DimensionKey, value: PlanKeyValue) => {
  if (isGoogle) {
    try {
      const valueProp: any = {};
      valueProp[key] = value;
      valueProp['send_to'] = process.env['GA_TRACKING_ID'];
      window.gtag('event', `${key}_dimension`, valueProp);
    } catch (error) {
      console.debug(error);
    }
  }
};

// const getAmplitude = async () => {
//   if (process.env['AMPLITUDE_TRACKING_ID']) {
//     // const pageViewTracking = pageViewTrackingPlugin({
//     //   trackOn: undefined,
//     //   trackHistoryChanges: 'pathOnly',
//     // });
//     // amplitude.add(pageViewTracking);
//     const amplitude = await import('@amplitude/analytics-browser');
//     amplitude.init(String(process.env['AMPLITUDE_TRACKING_ID']), {
//       defaultTracking: false,
//       // defaultTracking: {
//       //   attribution: false,
//       //   pageViews: false,
//       //   sessions: false,
//       //   formInteractions: false,
//       //   fileDownloads: false,
//       // }
//     });
//     return amplitude;
//   }

//   return null;
// };

export const purchase = async (viewer: ViewerItem, data: PurchaseData) => {
  data.planId = Number(data.items[0].id);

  await sendInternalEvent('plan update', {
    planId: data.planId,
    value: data.value,
    name: data.items[0].name,
    category: data.items[0].category || null,
    variant: data.items[0].variant || null,
    price: data.items[0].price,
    quantity: data.items[0].quantity,
    currency: data.currency || null,
    tax: data.tax || null,
  });

  if (isGoogle) {
    try {
      window.gtag('config', process.env['GA_TRACKING_ID']);
      window.gtag('event', 'purchase', {
        transaction_id: data.transactionId,
        planId: data.planId,
        value: data.value,
        tax: data.tax,
        currency: data.currency,
        items: data.items,
        send_to: process.env['GA_TRACKING_ID'],
      });
    } catch (error) {
      console.debug(error);
    }
  }
};

export const purchaseService = async (data: PurchaseServiceData) => {
  if (isGoogle) {
    try {
      window.gtag('config', process.env['GA_TRACKING_ID']);
      window.gtag('event', 'service purchase', {
        transaction_id: data.transactionId,
        eventTypeId: data.eventTypeId,
        value: data.value,
        tax: data.tax,
        planId: data.planId,
        CalendarViewType: data.calendarViewType,
        currency: data.currency,
        items: data.items,
        send_to: process.env['GA_TRACKING_ID'],
      });
    } catch (error) {
      console.debug(error);
    }
  }
};

export const setUserId = async (userId: string) => {
  if (isGoogle) {
    try {
      // https://developers.google.com/analytics/devguides/collection/gtagjs/cookies-user-id?hl=ja
      window.gtag('config', process.env['GA_TRACKING_ID'], {
        user_id: userId,
      });
    } catch (error) {
      console.debug(error);
    }
  }
};

export const setEvent = async (
  action: string,
  data: any = {},
  options?: {
    isInternal?: boolean;
    isGoogleAnalytics?: boolean;
  },
) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }
  if (options?.isInternal !== false) {
    await sendInternalEvent(action, data);
  }
  if (isGoogle) {
    if (options?.isGoogleAnalytics !== false) {
      try {
        window.gtag('config', process.env['GA_TRACKING_ID']);
        window.gtag('event', action, {
          event_category: data.category,
          event_label: data.label,
          value: data.value,
          send_to: process.env['GA_TRACKING_ID'],
        });
      } catch (error) {
        console.debug(error);
      }
    }
  }
};

export async function login(user: UserItem | null) {
  if (!user) {
    console.debug('user not set.');
    return;
  }
  Amplitude.login(user);
  Mixpanel.login(user);
}

export async function signUp(
  providerType: ProviderType,
  user: UserItem | null,
) {
  if (!user) {
    console.debug('user not set.');
    return;
  }
  if (!user.email) return;
  try {
    const props: any = {};
    props['source'] = Cookie.get('cp_s');
    props['medium'] = Cookie.get('cp_m');
    props['campaign'] = Cookie.get('cp_n');
    props['term'] = Cookie.get('cp_t');
    props['content'] = Cookie.get('cp_c');
    props['referrer'] = Cookie.get('cp_r');
    props['url'] = Cookie.get('cp_u');

    setEvent('sign up', {
      userId: user.uid,
      providerType,
      ...props,
    });
    Amplitude.signUp(user, {
      providerType,
      source: props['source'],
      medium: props['medium'],
      campaign: props['campaign'],
      term: props['term'],
      content: props['content'],
      referrer: props['referrer'],
      landingUrl: props['url'],
    });
    Mixpanel.signUp(user, {
      providerType,
      source: props['source'],
      medium: props['medium'],
      campaign: props['campaign'],
      term: props['term'],
      content: props['content'],
      referrer: props['referrer'],
      landingUrl: props['url'],
    });
  } catch (error) {
    console.debug(error);
  }
}

export function logout() {}

export const setTiming = (data: any = {}) => {
  if (isGoogle) {
    try {
      window.gtag('config', process.env['GA_TRACKING_ID']);
      window.gtag('event', 'timing_complete', {
        name: data.name,
        value: data.value,
        event_category: data.category,
        event_label: data.label,
        send_to: process.env['GA_TRACKING_ID'],
      });
    } catch (error) {
      console.debug(error);
    }
  }
};

export const beginPerform = () => {
  return new Date().getTime();
};

export const trackClickEvent = (
  e: SyntheticEvent<HTMLAnchorElement | HTMLDivElement>,
): void => {
  const { eventName } = e.currentTarget.dataset;
  if (!eventName) return;
  setEvent(eventName);
};

export const sendInternalEvent = async (
  name: string,
  data: { [key: string]: string | number | null } | null = null,
) => {
  const body: CreateInternalActivityLogBody = {
    name,
    data,
    userAgent: window.navigator.userAgent,
    url: window.location.href,
    locale: currentLocale(),
    timeZone: currentTimeZone(),
  };
  const result = await post(apiPath(`/_internal/activity_logs/create`), {
    data: body,
  });
  if (!result.isSuccess) {
    console.warn('notification token create failed');
  }
};
